import React, { FC } from 'react';
import { Box, Typography, Divider } from '@mui/material';
import { Training } from '../../../models/training.models';
import {
  formatDuration,
  getElevationMeasurementUnit,
  getMeasurementUnit,
  getSpeedMeasurementUnit,
} from '../../../helpers/general.helpers';
import COETrainingRouteMapComponent from '../../coe-maps/map.component';
import { palette } from '../../../theme';

export interface TrainingModalSummaryProps {
  training: Training;
}

/** Convert speed (mph or km/h) to a pace string "M:SS" */
function formatPace(speedValue: number): string {
  if (!speedValue || speedValue <= 0) return '—';
  const minPerUnit = 60 / speedValue;
  const mins = Math.floor(minPerUnit);
  const secs = Math.round((minPerUnit - mins) * 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

const statLabelSx = {
  textTransform: 'uppercase' as const,
  letterSpacing: '0.08em',
  fontWeight: 600,
  lineHeight: 1.2,
  display: 'block',
  mb: 0.25,
};

const TrainingModalSummary: FC<TrainingModalSummaryProps> = ({ training }) => {
  const isComplete = training?.workoutData?.complete ?? false;
  const hasSamples = (training?.workoutData?.samples?.length ?? 0) > 0;

  if (!training.workoutData && !isComplete) {
    return null;
  }

  return (
    <Box sx={{ px: 2.5 }}>
      {/* Completed state: Map + Stats grid */}
      {isComplete && training.workoutData && (
        <Box
          sx={{
            display: 'flex',
            gap: 0,
            mb: 2,
            height: '170px',
            border: `1px solid ${palette.grey[200]}`,
            borderRadius: '12px',
            overflow: 'hidden',
          }}
        >
          {/* Map — only when GPS samples exist */}
          {hasSamples && (
            <Box
              sx={{
                width: '40%',
                flexShrink: 0,
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                borderRadius: '12px 0 0 12px',
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  '& .map-container': { borderRadius: '12px 0 0 12px', boxShadow: 'none' },
                  '& .map': { borderRadius: '12px 0 0 12px', boxShadow: 'none' },
                }}
              >
                <COETrainingRouteMapComponent
                  coordinates={training.workoutData.samples
                    .filter((s: any) => s.longitude && s.latitude)
                    .map((s: any) => [s.longitude, s.latitude])}
                  width="100%"
                  height="100%"
                />
              </Box>
              {training.workoutData.deviceName && (
                <Box sx={{ px: 1.5, py: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    Uploaded from{' '}
                    <Typography component="span" variant="caption" fontWeight={700} color="text.primary">
                      {training.workoutData.deviceName}
                    </Typography>
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {/* Stats grid */}
          <Box
            sx={{
              width: hasSamples ? '60%' : '100%',
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              borderLeft: hasSamples ? `1px solid ${palette.grey[200]}` : 'none',
            }}
          >
            {[
              {
                label: 'Duration',
                value: formatDuration(training.workoutData.totalDurationComplete),
                unit: 'hr',
              },
              {
                label: 'Distance',
                value: training.workoutData.totalDistanceComplete,
                unit: getMeasurementUnit(),
              },
              {
                label: 'Ascent',
                value: training.workoutData.totalElevationGain,
                unit: getElevationMeasurementUnit(),
              },
              {
                label: 'Avg HR',
                value: training.workoutData.averageHeartRate || '—',
                unit: 'bpm',
              },
              {
                label: 'Max HR',
                value: training.workoutData.maxHeartRate || '—',
                unit: 'bpm',
              },
              {
                label: 'Avg Pace',
                value: formatPace(training.workoutData.averageSpeed),
                unit: `/${getMeasurementUnit()}`,
              },
              {
                label: 'Calories',
                value: training.workoutData.kilocalories || '—',
                unit: '',
              },
              {
                label: 'Avg Speed',
                value: training.workoutData.averageSpeed,
                unit: getSpeedMeasurementUnit(),
              },
              {
                label: 'Moving',
                value: formatDuration(training.workoutData.totalDurationComplete),
                unit: 'hr',
              },
            ].map((stat, i) => (
              <Box
                key={stat.label}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  px: 1,
                  py: 0.5,
                  borderRight: (i + 1) % 3 !== 0 ? `1px solid ${palette.grey[200]}` : 'none',
                  borderBottom: i < 6 ? `1px solid ${palette.grey[200]}` : 'none',
                }}
              >
                <Typography variant="caption" color="text.secondary" sx={statLabelSx}>
                  {stat.label}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.25 }}>
                  <Typography variant="subtitle1" fontWeight={700} color={palette.primary.main} lineHeight={1.1}>
                    {stat.value}
                  </Typography>
                  {stat.unit && (
                    <Typography variant="caption" color="text.secondary">
                      {stat.unit}
                    </Typography>
                  )}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* Planned state: Stat boxes */}
      {!isComplete && (
        <Box sx={{ display: 'flex', gap: 3, mb: 2 }}>
          {/* Duration */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
              <Typography variant="h5" fontWeight={700} lineHeight={1}>
                {formatDuration(training.totalDurationPlanned)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                hr
              </Typography>
            </Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600 }}
            >
              Duration
            </Typography>
          </Box>

          <Divider orientation="vertical" flexItem />

          {/* Distance */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
              <Typography variant="h5" fontWeight={700} lineHeight={1}>
                {training.totalDistancePlanned ?? 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {getMeasurementUnit()}
              </Typography>
            </Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600 }}
            >
              Distance
            </Typography>
          </Box>

          <Divider orientation="vertical" flexItem />

          {/* Ascent */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
              <Typography variant="h5" fontWeight={700} lineHeight={1}>
                {training.workoutData?.totalElevationGain ?? 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {getElevationMeasurementUnit()}
              </Typography>
            </Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600 }}
            >
              Ascent
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default TrainingModalSummary;
